/**
 * The split the customer gives when they accept a line of versions that offers several quantities (supplier interface
 * spec 53, ruling 40): each version's quantity for the quantity they chose. The backend checks it (one quantity per
 * version, each at least 1, adding up to the quantity chosen); these rules only let the page show the running sum and fill
 * in an even split, so the customer sees what the backend will accept before they press Accept.
 */

/** One entry per version, in version order; `null` is a version not yet given a quantity. */
export type VersionSplitEntry = readonly (number | null)[];

/**
 * The quantity shared evenly between `count` versions, the remainder one each to the first versions (1,000 in 3 is
 * 334, 333, 333): what the "Split evenly" button fills in. The customer may type any split that adds up, so no step of
 * the item's applies here.
 */
export function evenSplit(total: number, count: number): number[] {
  const units = Math.floor(total / count);
  const remainder = total % count;
  return Array.from(
    { length: count },
    (_, position) => units + (position < remainder ? 1 : 0),
  );
}

/** What the versions' entered quantities add up to so far (a version not given counts for nothing). */
export function splitSum(entry: VersionSplitEntry): number {
  return entry.reduce<number>((sum, quantity) => sum + (quantity ?? 0), 0);
}

/** Every version has a whole quantity of at least 1 and together they make `total`: the backend will accept it. */
export function isSplitComplete(
  entry: VersionSplitEntry,
  total: number,
): boolean {
  return (
    entry.length > 0 &&
    entry.every(
      (quantity) => Number.isInteger(quantity) && (quantity ?? 0) >= 1,
    ) &&
    splitSum(entry) === total
  );
}

/** The running sum beside the inputs: "1,000 of 1,000 given", or what is left or over ("200 left to give"). */
export function splitSummary(entry: VersionSplitEntry, total: number): string {
  const given = splitSum(entry);
  const inFull = total.toLocaleString("en-GB");
  if (given === total) return `${inFull} of ${inFull} given`;
  if (given < total)
    return `${(total - given).toLocaleString("en-GB")} left to give`;
  return `${(given - total).toLocaleString("en-GB")} over ${inFull}`;
}

/** An input's text as a quantity: a whole number of at least 1, else none yet. */
export function parseSplitQuantity(text: string): number | null {
  const value = Number(text.replace(/,/g, "").trim());
  return text.trim() !== "" && Number.isInteger(value) && value >= 1
    ? value
    : null;
}
